// FILE: frontend/src/utils/SecurityUtils.ts
// Complete Security Utilities Module

export type UserRole = 'admin' | 'manager' | 'boss';

interface TokenData {
  token: string;
  sessionId: string;
  expiryTime: number;
  role: UserRole;
  lastActivity: number;
  fingerprint?: string;
}

class SecurityUtilsClass {
  private static readonly TOKEN_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
  private static readonly INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  private static readonly MAX_LOGIN_ATTEMPTS = 5;
  private static readonly LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

  /**
   * Encrypt sensitive data
   */
  encrypt(data: string): string {
    try {
      const encoded = btoa(encodeURIComponent(data));
      const salt = Math.random().toString(36).substring(2, 10);
      return `${salt}.${encoded}`;
    } catch (error) {
      console.error('Encryption error:', error);
      return data;
    }
  }

  /**
   * Decrypt stored data
   */
  decrypt(data: string): string {
    try {
      if (!data.includes('.')) return data;
      const [, encoded] = data.split('.');
      return decodeURIComponent(atob(encoded));
    } catch (error) {
      console.error('Decryption error:', error);
      return data;
    }
  }

  /**
   * Generate unique session ID
   */
  generateSessionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const random2 = Math.random().toString(36).substring(2, 15);
    return `${timestamp}_${random}_${random2}`;
  }

  /**
   * Generate browser fingerprint
   */
  generateFingerprint(): string {
    const nav = window.navigator;
    const scr = window.screen;
    
    const fingerprint = [
      nav.userAgent,
      nav.language,
      scr.colorDepth,
      scr.width,
      scr.height,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage
    ].join('|');

    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  /**
   * Get token storage key
   */
  getTokenKey(role: UserRole): string {
    const keys: Record<UserRole, string> = {
      admin: 'token',
      manager: 'managerToken',
      boss: 'bossToken'
    };
    return keys[role];
  }

  /**
   * Store token securely
   */
  setSecureToken(role: UserRole, token: string, expiresIn: number = SecurityUtilsClass.TOKEN_EXPIRY): void {
    const sessionId = this.generateSessionId();
    const fingerprint = this.generateFingerprint();
    const expiryTime = Date.now() + expiresIn;
    
    const tokenData: TokenData = {
      token: this.encrypt(token),
      sessionId,
      expiryTime,
      role,
      lastActivity: Date.now(),
      fingerprint
    };

    const tokenKey = this.getTokenKey(role);
    localStorage.setItem(tokenKey, JSON.stringify(tokenData));
    localStorage.setItem(`${tokenKey}_session`, sessionId);
    sessionStorage.setItem('auth_active', 'true');
    sessionStorage.setItem('auth_role', role);
    localStorage.setItem('login_timestamp', Date.now().toString());

    console.log(`✅ Token stored securely for ${role}`);
  }

  /**
   * Retrieve and validate token
   */
  getSecureToken(role: UserRole): string | null {
    const tokenKey = this.getTokenKey(role);
    const storedData = localStorage.getItem(tokenKey);
    const storedSession = localStorage.getItem(`${tokenKey}_session`);

    if (!storedData || !storedSession) {
      return null;
    }

    try {
      const tokenData: TokenData = JSON.parse(storedData);

      // Validate session ID
      if (tokenData.sessionId !== storedSession) {
        console.warn('⚠️ Session ID mismatch');
        this.clearAllTokens();
        return null;
      }

      // Check expiration
      if (Date.now() > tokenData.expiryTime) {
        console.warn('⚠️ Token expired');
        this.clearAllTokens();
        return null;
      }

      // Check inactivity
      if (Date.now() - tokenData.lastActivity > SecurityUtilsClass.INACTIVITY_TIMEOUT) {
        console.warn('⚠️ Session timeout');
        this.clearAllTokens();
        return null;
      }

      // Validate fingerprint
      const currentFingerprint = this.generateFingerprint();
      if (tokenData.fingerprint && tokenData.fingerprint !== currentFingerprint) {
        console.warn('⚠️ Fingerprint mismatch');
        this.clearAllTokens();
        return null;
      }

      // Update activity
      tokenData.lastActivity = Date.now();
      localStorage.setItem(tokenKey, JSON.stringify(tokenData));

      return this.decrypt(tokenData.token);
    } catch (error) {
      console.error('Token validation error:', error);
      this.clearAllTokens();
      return null;
    }
  }

  /**
   * Clear all tokens
   */
  clearAllTokens(): void {
    const keys = [
      'token', 'managerToken', 'bossToken',
      'admin', 'manager', 'boss',
      'login_timestamp'
    ];
    
    keys.forEach(key => {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}_session`);
    });
    
    sessionStorage.removeItem('auth_active');
    sessionStorage.removeItem('auth_role');
    sessionStorage.removeItem('security_initialized');
    
    console.log('🧹 All authentication data cleared');
  }

  /**
   * Validate JWT token format
   */
  isValidTokenFormat(token: string): boolean {
    if (!token || typeof token !== 'string') return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    if (token.length < 20) return false;
    const base64UrlRegex = /^[A-Za-z0-9\-_]+$/;
    return parts.every(part => base64UrlRegex.test(part));
  }

  /**
   * Check rate limit
   */
  checkRateLimit(identifier: string): boolean {
    const key = `login_attempts_${identifier}`;
    const attempts = localStorage.getItem(key);
    
    if (!attempts) return true;

    try {
      const data = JSON.parse(attempts);
      const now = Date.now();
      
      if (now - data.firstAttempt > SecurityUtilsClass.LOCKOUT_DURATION) {
        localStorage.removeItem(key);
        return true;
      }

      return data.count < SecurityUtilsClass.MAX_LOGIN_ATTEMPTS;
    } catch {
      localStorage.removeItem(key);
      return true;
    }
  }

  /**
   * Record failed attempt
   */
  recordFailedAttempt(identifier: string): void {
    const key = `login_attempts_${identifier}`;
    const attempts = localStorage.getItem(key);
    
    if (!attempts) {
      localStorage.setItem(key, JSON.stringify({
        count: 1,
        firstAttempt: Date.now()
      }));
    } else {
      try {
        const data = JSON.parse(attempts);
        data.count += 1;
        data.lastAttempt = Date.now();
        localStorage.setItem(key, JSON.stringify(data));
        
        if (data.count >= SecurityUtilsClass.MAX_LOGIN_ATTEMPTS) {
          console.warn(`🔒 Account locked after ${data.count} failed attempts`);
        }
      } catch {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Clear failed attempts
   */
  clearFailedAttempts(identifier: string): void {
    const key = `login_attempts_${identifier}`;
    localStorage.removeItem(key);
  }

  /**
   * Get remaining lockout time
   */
  getRemainingLockoutTime(identifier: string): number {
    const key = `login_attempts_${identifier}`;
    const attempts = localStorage.getItem(key);
    
    if (!attempts) return 0;

    try {
      const data = JSON.parse(attempts);
      const elapsed = Date.now() - data.firstAttempt;
      const remaining = SecurityUtilsClass.LOCKOUT_DURATION - elapsed;
      return Math.ceil(remaining / 60000);
    } catch {
      return 0;
    }
  }

  /**
   * Sanitize input
   */
  sanitizeInput(input: string): string {
    return input
      .trim()
      .replace(/[<>]/g, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  }

  /**
   * Validate email
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Check if token expiring soon
   */
  isTokenExpiringSoon(role: UserRole): boolean {
    const tokenKey = this.getTokenKey(role);
    const storedData = localStorage.getItem(tokenKey);
    
    if (!storedData) return false;

    try {
      const tokenData: TokenData = JSON.parse(storedData);
      const timeUntilExpiry = tokenData.expiryTime - Date.now();
      return timeUntilExpiry < 5 * 60 * 1000;
    } catch {
      return false;
    }
  }

  /**
   * Refresh token activity
   */
  refreshTokenActivity(role: UserRole): void {
    const tokenKey = this.getTokenKey(role);
    const storedData = localStorage.getItem(tokenKey);
    
    if (!storedData) return;

    try {
      const tokenData: TokenData = JSON.parse(storedData);
      tokenData.lastActivity = Date.now();
      localStorage.setItem(tokenKey, JSON.stringify(tokenData));
    } catch (error) {
      console.error('Failed to refresh token activity:', error);
    }
  }

  /**
   * Get user data securely
   */
  getSecureUserData(role: UserRole): any | null {
    const userKey = role;
    const storedData = localStorage.getItem(userKey);
    
    if (!storedData) return null;

    try {
      if (storedData.includes('.')) {
        const decrypted = this.decrypt(storedData);
        return JSON.parse(decrypted);
      }
      return JSON.parse(storedData);
    } catch (error) {
      console.error('Failed to retrieve user data:', error);
      return null;
    }
  }

  /**
   * Set user data securely
   */
  setSecureUserData(role: UserRole, userData: any): void {
    const userKey = role;
    const jsonData = JSON.stringify(userData);
    const encrypted = this.encrypt(jsonData);
    localStorage.setItem(userKey, encrypted);
  }

  /**
   * Setup tamper detection
   */
  setupTamperDetection(onTamperDetected: () => void): () => void {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.includes('token') || e.key?.includes('session')) {
        if (e.oldValue !== e.newValue && e.newValue !== null) {
          console.error('🚨 Token tampering detected!');
          this.clearAllTokens();
          onTamperDetected();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }

  /**
   * Setup auto refresh
   */
  setupAutoRefresh(role: UserRole, intervalMs: number = 60000): () => void {
    const interval = setInterval(() => {
      this.refreshTokenActivity(role);
      
      if (this.isTokenExpiringSoon(role)) {
        console.warn('⚠️ Token expiring soon');
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }

  /**
   * Validate session
   */
  isSessionValid(role: UserRole): boolean {
    const token = this.getSecureToken(role);
    const authActive = sessionStorage.getItem('auth_active');
    const authRole = sessionStorage.getItem('auth_role');
    
    return !!(token && authActive === 'true' && authRole === role);
  }

  /**
   * Get secure headers for API
   */
  getSecureHeaders(role: UserRole): HeadersInit {
    const token = this.getSecureToken(role);
    
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
      'X-Client-Version': '1.0.0',
      'X-Request-ID': this.generateSessionId(),
      'X-Fingerprint': this.generateFingerprint()
    };
  }

  /**
   * Log security event
   */
  logSecurityEvent(event: string, details?: any): void {
    const log = {
      timestamp: new Date().toISOString(),
      event,
      details,
      userAgent: navigator.userAgent
    };
    
    console.log('🔐 Security Event:', log);
  }

  /**
   * Check for XSS
   */
  containsXSS(input: string): boolean {
    const xssPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe/i,
      /eval\(/i,
      /expression\(/i
    ];
    
    return xssPatterns.some(pattern => pattern.test(input));
  }
}

const SecurityUtils = new SecurityUtilsClass();
export default SecurityUtils;